<script setup lang="ts">
import {CSSProperties, ref, useTemplateRef} from "vue";
import {useEventListener} from "@vueuse/core";

const props = withDefaults(defineProps<{
  supportResize?: boolean,
  defaultHeight?: CSSProperties['height']
}>(), {supportResize: true, defaultHeight: "auto"});
const emit = defineEmits<{
  (e: "stopResize", height: CSSProperties['height']): void
}>()
const isResizing = ref(false);
const startY = ref(0);
const hasInitialize = ref(false);
const startHeight = ref(0);
const containerRef = useTemplateRef<HTMLDivElement>("containerRef");
const height = ref<CSSProperties['height']>(props.defaultHeight ?? "auto");

function startResize(event: MouseEvent) {
  isResizing.value = true;
  startY.value = event.clientY;
  if (height.value === "auto" && containerRef.value) {
    startHeight.value = containerRef.value.offsetHeight;
  } else if (typeof height.value === "number") {
    startHeight.value = height.value;
  }
  event.preventDefault();
  document.body.style.cursor = 'ns-resize';
  document.body.style.userSelect = 'none';
}

function onMouseMove(event: MouseEvent) {
  if (!isResizing.value)
    return;
  hasInitialize.value = true;
  const deltaY = event.clientY - startY.value;
  height.value = startHeight.value + deltaY;
}

function stopResize() {
  if (isResizing.value) {
    isResizing.value = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    emit("stopResize", height.value)
  }
}

if (props.supportResize) {
  useEventListener(document, 'mousemove', onMouseMove);
  useEventListener(document, 'mouseup', stopResize);
}

</script>

<template>
  <div :style="{height: height === 'auto' ? 'auto' : height + 'px'}" ref="containerRef" class="tw-relative">
    <div class="tw-h-full tw-overflow-y-auto tw-max-h-full">
      <slot :isResizing="isResizing" :height="height"/>
    </div>
    <div
      v-if="supportResize"
      @mousedown="startResize"
      class="tw-group/editor tw-relative tw-h-2 tw-w-full tw-cursor-ns-resize tw-flex tw-items-center tw-justify-center tw-select-none"
      :class="{'tw-bg-primary-light/20 dark:tw-bg-primary-dark/20': isResizing}"
      title="Drag to resize editor">
      <div class="tw-flex tw-flex-col tw-gap-[2px] tw-py-0.5">
        <span
          class="tw-h-[2px] tw-w-12 tw-rounded-full tw-bg-gray-400 dark:tw-bg-gray-500 group-hover/editor:tw-bg-primary-light dark:group-hover/editor:tw-bg-primary-dark tw-transition-colors"
          :class="{'tw-bg-primary-light dark:tw-bg-primary-dark': isResizing}"/>
        <span
          class="tw-h-[2px] tw-w-12 tw-rounded-full tw-bg-gray-400 dark:tw-bg-gray-500 group-hover/editor:tw-bg-primary-light dark:group-hover/editor:tw-bg-primary-dark tw-transition-colors"
          :class="{'tw-bg-primary-light dark:tw-bg-primary-dark': isResizing}"/>
      </div>
    </div>
  </div>
</template>
